import { useImmer } from "use-immer";
import { useEffect } from "react";
import { errorMessage, successMessage } from "../../../utils";
import { usePermission } from "../../../shared";
import {
  getStampCardCustomersApi,
  deleteStampCardCustomerApi,
  assignCustomerToStampCardApi,
} from "../api";
import { getCustomers } from "../../../shared/api/dropdownApi";
import qs from "qs";
import { useDebouncedCallback } from "use-debounce";

export const useStampCardCustomers = ({ stampCardId }) => {
  const isDeletable = usePermission("stamp-card-delete");
  const isEditable = usePermission("stamp-card-modify");

  const [state, setState] = useImmer({
    isBusy: false,
    isDelete: false,
    deleteId: null,
    isApplyAllConfirmOpen: false,
    customers: [],
    searchText: "",
    assign: {
      open: false,
      customers: [],
      selectedUsers: [],
      selectAll: false,
      pagination: {
        pageIndex: 1,
        pageSize: 10,
        hasMorePages: true,
      },
      isLoading: false,
    },
  });
  const fetchStampCardCustomers = async () => {
    if (!stampCardId) return;

    try {
      setState((draft) => {
        draft.isBusy = true;
      });

      let params = {};
      const query = {
        params,
        paramsSerializer: (p) => qs.stringify(p),
      };

      const res = await getStampCardCustomersApi(stampCardId, query);

      if (res.success) {
        setState((draft) => {
          draft.customers = res.data || [];
          draft.isBusy = false;
        });
      } else {
        setState((draft) => {
          draft.isBusy = false;
        });
      }
    } catch (err) {
      errorMessage(err?.response?.data?.message || "Failed to load customers");
      setState((draft) => {
        draft.isBusy = false;
      });
    }
  };

  useEffect(() => {
    if (stampCardId) {
      fetchStampCardCustomers();
    }
  }, [stampCardId]);

  const DeleteModals = (isOpen, customerId) => {
    setState((draft) => {
      draft.isDelete = isOpen;
      draft.deleteId = customerId || null;
    });
  };

  const handleDeleteCustomer = async () => {
    try {
      setState((draft) => {
        draft.isBusy = true;
      });
      const res = await deleteStampCardCustomerApi(stampCardId, state.deleteId);
      if (res.success) {
        successMessage(res.message || "Customer removed successfully");
        await fetchStampCardCustomers();
        setState((draft) => {
          draft.isBusy = false;
          draft.isDelete = false;
          draft.deleteId = null;
        });
      } else {
        errorMessage(res.message || "Failed to delete customer");
        setState((draft) => {
          draft.isBusy = false;
        });
      }
    } catch (err) {
      errorMessage(err?.response?.data?.message || "Failed to delete customer");
      setState((draft) => {
        draft.isBusy = false;
      });
    }
  };

  const loadCustomersPage = async ({ page, search, type }) => {
    try {
      const pagination = {
        pageIndex: page,
        pageSize: state.assign.pagination.pageSize,
      };

      let params = {};
      if (search) {
        params = { ...params, search };
      }

      params = { ...params, not_deleted_user_search: 1 };

      const query = {
        params,
        paramsSerializer: (p) => qs.stringify(p),
      };

      const res = await getCustomers(query, pagination);
      if (res.success) {
        const customers = res.data.customers || [];
        setState((draft) => {
          draft.assign.customers =
            type === "search"
              ? customers
              : [...draft.assign.customers, ...customers];
          draft.assign.pagination.pageIndex = page;
          draft.assign.pagination.hasMorePages =
            res.data.pagination?.hasMorePages ?? false;
          draft.assign.isLoading = false;
        });
      } else {
        setState((draft) => {
          draft.assign.isLoading = false;
        });
      }
    } catch (err) {
      errorMessage(err?.response?.data?.message || "Failed to load customers");
      setState((draft) => {
        draft.assign.isLoading = false;
      });
    }
  };

  const openAssignModal = () => {
    setState((draft) => {
      draft.assign.open = true;
      draft.assign.customers = [];
      draft.assign.searchText = "";
      // Pre-select already assigned customers in the modal
      draft.assign.selectedUsers = draft.customers.map((customer) => customer.id);
      draft.assign.selectAll = false;
      draft.assign.pagination.pageIndex = 1;
      draft.assign.pagination.hasMorePages = true;
      draft.assign.isLoading = true;
    });
    loadCustomersPage({ page: 1, search: "", type: "initial" });
  };

  const closeAssignModal = () => {
    setState((draft) => {
      draft.assign.open = false;
      draft.assign.selectedUsers = [];
      draft.assign.selectAll = false;
    });
  };

  const loadNextPage = () => {
    if (!state.assign.pagination.hasMorePages || state.assign.isLoading) return;
    setState((draft) => {
      draft.assign.isLoading = true;
    });
    loadCustomersPage({
      page: state.assign.pagination.pageIndex + 1,
      search: state.assign.searchText,
      type: "append",
    });
  };

  const handleUsersSelect = (id) => {
    if (state.assign.selectAll) return;
    setState((draft) => {
      if (draft.assign.selectedUsers.includes(id)) {
        draft.assign.selectedUsers = draft.assign.selectedUsers.filter(
          (val) => val !== id,
        );
      } else {
        draft.assign.selectedUsers = [...draft.assign.selectedUsers, id];
      }
    });
  };

  const isSelectedUser = (id) => {
    if (state.assign.selectAll) return true;
    return state.assign.selectedUsers.includes(id);
  };

  const HandleFilterUsers = (type, text) => {
    setState((draft) => {
      draft.assign.searchText = text;
      draft.assign.isLoading = true;
      draft.assign.customers = [];
      draft.assign.pagination.pageIndex = 1;
      draft.assign.pagination.hasMorePages = true;
      draft.assign.selectAll = false;
    });
    loadCustomersPage({ page: 1, search: text, type: "search" });
  };

  const handleAssignCustomer = async () => {
    if (!state.assign.selectAll && !state.assign.selectedUsers.length) return;
    try {
      setState((draft) => {
        draft.isBusy = true;
      });

      const payload = state.assign.selectAll
        ? {
            select_all: true,
          }
        : {
            select_all: false,
            customer_ids: state.assign.selectedUsers,
          };

      const res = await assignCustomerToStampCardApi(stampCardId, payload);

      if (res.success) {
        successMessage(res.message || "Customer added successfully");
        await fetchStampCardCustomers();
        setState((draft) => {
          draft.isBusy = false;
          draft.assign.open = false;
          draft.assign.selectedUsers = [];
          draft.assign.selectAll = false;
        });
      } else {
        errorMessage(res.message || "Failed to add customer");
        setState((draft) => {
          draft.isBusy = false;
        });
      }
    } catch (err) {
      errorMessage(
        err?.response?.data?.message || "Failed to add customer to stamp card",
      );
      setState((draft) => {
        draft.isBusy = false;
      });
    }
  };

  const toggleSelectAll = (value) => {
    setState((draft) => {
      draft.assign.selectAll = value;
      if (value) {
        draft.assign.selectedUsers = [];
      }
    });
  };

  const applyToAllCustomers = async () => {
    try {
      setState((draft) => {
        draft.isBusy = true;
      });

      const res = await assignCustomerToStampCardApi(stampCardId, {
        select_all: true,
      });

      if (res.success) {
        successMessage(res.message || "Customer added successfully");
        handleApplyAllConfirmModal(false);
        await fetchStampCardCustomers();
        setState((draft) => {
          draft.isBusy = false;
        });
      } else {
        errorMessage(res.message || "Failed to add customer");
        setState((draft) => {
          draft.isBusy = false;
        });
      }
    } catch (err) {
      errorMessage(
        err?.response?.data?.message || "Failed to add customer to stamp card",
      );
      setState((draft) => {
        draft.isBusy = false;
      });
    }
  };

  const handleApplyAllConfirmModal = (isOpen) => {
    setState((draft) => {
      draft.isApplyAllConfirmOpen = isOpen;
    });
  };

  return {
    state,
    isDeletable,
    isEditable,
    DeleteModals,
    handleDeleteCustomer,
    toggleSelectAll,
    applyToAllCustomers,
    handleApplyAllConfirmModal,
    openAssignModal,
    closeAssignModal,
    handleUsersSelect,
    isSelectedUser,
    loadNextPage,
    HandleFilterUsers,
    handleAssignCustomer,
  };
};
